<script setup lang="ts">
import { isElementInViewport } from '@@/bits/dom'
import { __ } from '@@/bits/intl'
import { defineAsyncComponent } from '@@/bits/vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { OzPlainButtonColorScheme, OzPlainButtonSizePreset } from '@@/library/v4/components/OzPlainButton.vue'
import { OzSkewedButtonSizePreset } from '@@/library/v4/components/OzSkewedButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { LabelledSection } from '@@/types'
import SurfaceSectionTitle from '@@/vuecomponents/SurfaceSectionTitle.vue'
import type { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const OzSkewedButton = defineAsyncComponent(() => import('@@/library/v4/components/OzSkewedButton.vue'))
const OzPlainButton = defineAsyncComponent(() => import('@@/library/v4/components/OzPlainButton.vue'))
const OzIcon = defineAsyncComponent(() => import('@@/library/v4/components/OzIcon.vue'))

const props = defineProps<{
  section: Required<LabelledSection>
  useDefaultBgColor?: boolean
  hideButtons?: boolean
}>()

const emit = defineEmits<{
  (name: 'long-press', event: LongPressEvent): void
  (name: 'resize', value: DOMRectReadOnly): void
  (name: 'before-unmount'): void
}>()

const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const surfaceMapStore = useSurfaceMapStore()
const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()

const {
  backgroundColor,
  backgroundLuminance,
  colorScheme,
  dir,
  fontId,
  format,
  isGrid,
  isMap,
  isReadOnly,
  skinPrimaryVariantColorAsRGB,
  isSectionBreakout,
  isFrozen,
  isMatrix,
  isShelf,
  isStream,
  isTimeline,
  isTimelineV1,
  isTable,
} = storeToRefs(useSurfaceStore())
const { isDraggingPostInNonManuallySortedWall } = storeToRefs(useSurfacePostsDragAndDropStore())
const { canIModerate, canIPost } = storeToRefs(useSurfacePermissionsStore())
const {
  sectionBeingRenamedId,
  canEditSectionNameInline,
  xSectionSpecificButtons,
  sectionUnderActionId,
  sectionUnderActionRightClickCoordinates,
} = storeToRefs(surfaceSectionsStore)

const shouldHideSectionButtonsWhenDragging = computed(
  () => !isShelf.value && isDraggingPostInNonManuallySortedWall.value,
)

function calculateMenuPositionAvoidingOnboardingPanel(x: number): { isCovered: boolean; adjustedX: number } {
  const onboardingPanel = document.getElementById('surface-blank-onboarding-panel')
  if (!onboardingPanel) return { isCovered: false, adjustedX: x }

  const onboardingPanelRect = onboardingPanel.getBoundingClientRect()
  const menuWidth = 256 // Width of the action menu

  if (x + menuWidth > onboardingPanelRect.left) {
    const adjustedX = onboardingPanelRect.left - menuWidth - 8 // 8px buffer from the onboarding panel
    return { isCovered: true, adjustedX }
  }

  return { isCovered: false, adjustedX: x }
}

function handleOpenSectionActionMenu(sectionId: LabelledSection['id'], e?: MouseEvent): void {
  if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) return

  if (sectionId) {
    sectionUnderActionId.value = sectionId
    const isOpeningAtCoordinates = e?.pageX != null && e?.pageY != null

    if (isOpeningAtCoordinates) {
      if (surfaceOnboardingPanelStore.isBlankOnboardingPanel) {
        const { adjustedX } = calculateMenuPositionAvoidingOnboardingPanel(e.pageX)
        sectionUnderActionRightClickCoordinates.value = {
          x: adjustedX,
          y: e.pageY,
        }
      } else {
        sectionUnderActionRightClickCoordinates.value = { x: e.pageX, y: e.pageY }
      }
    } else {
      sectionUnderActionRightClickCoordinates.value = null
    }
  }
}

function startEditingSectionTitle(sectionId: LabelledSection['id']): void {
  if (sectionId) surfaceSectionsStore.startRenamingSection(sectionId)
}

function finishEditingSectionTitle(newTitle): void {
  if (sectionBeingRenamedId.value == null) return
  const edited = surfaceSectionsStore.editSectionTitle({
    sectionId: sectionBeingRenamedId.value,
    title: newTitle,
  })
  if (edited) {
    surfaceSectionsStore.stopRenamingSection()
  }
}

const $el = ref<HTMLElement | null>(null)
const addPostButtonRef = ref<InstanceType<typeof OzBaseButton> | null>(null)

function emitHeaderResize(): void {
  if ($el.value) {
    emit('resize', $el.value.getBoundingClientRect())
  }
}

watch(sectionBeingRenamedId, (newSectionBeingRenamedId) => {
  if (newSectionBeingRenamedId == null) return
  nextTick(() => {
    const element = document.getElementById(`section-title-${newSectionBeingRenamedId}`)
    if (element && !isElementInViewport(element)) {
      element?.scrollIntoView(true)
    }
  })
})

onBeforeUnmount(() => {
  emit('before-unmount')
})

const sectionAddPostButton = computed(() => ({
  shouldShow: canIPost.value && !isReadOnly.value && !isSectionBreakout.value && !isFrozen.value,
  icon: 'plus_thin',
  label: __('Add post to this section'),
  onClick: () => {
    if (isMap.value) {
      surfaceMapStore.startPickingLocation({ sectionId: props.section.id })
    } else {
      surfacePostsStore.startNewPost({ attributes: { wall_section_id: props.section.id } })
    }
  },
  id: `section-add-post-button-${props.section.id}`,
  testId: `${props.section.title}SectionAddPostButton`,
}))

const sectionActionsButton = computed(() => ({
  shouldShow: !isReadOnly.value && !isSectionBreakout.value,
  icon: 'dot_3_horizontal',
  label: __('Open section actions menu'),
  onClick: () => handleOpenSectionActionMenu(props.section.id),
  id: `section-action-menu-button-${props.section.id}`,
  testId: `${props.section.title}SectionTitleMoreButton`,
}))

const sectionSpecificButtons = computed(() =>
  [sectionAddPostButton.value, sectionActionsButton.value].filter((button) => button.shouldShow),
)

// There's a possibility that some elements inside $el is not rendered yet.
// Therefore we also need to watch for whenever child elements of $el are rendered/hidden.
watch(addPostButtonRef, () => {
  emitHeaderResize()
})

const sectionTitleRef = ref<ComponentPublicInstance | null>(null)

const sectionTitle = computed(() => {
  if (props.section.id === sectionBeingRenamedId.value && canEditSectionNameInline.value) {
    // If this section is being edited, fetch and use the original title
    // Fallback to the prop's title if raw isn't found (shouldn't happen ideally)
    return surfaceSectionsStore.getRawSectionById(props.section.id)?.title ?? props.section.title
  } else {
    return props.section.title
  }
})

defineExpose({
  sectionTitleRef,
})
</script>

<template>
  <header
    ref="$el"
    :class="{ flex: true, 'flex-col': isShelf, 'flex-row items-end': !isShelf, 'justify-between': isMap }"
    @long-press="$emit('long-press', $event)"
  >
    <SurfaceSectionTitle
      :id="section.ariaLabelledby"
      :ref="'sectionTitleRef'"
      :section-id="section.id"
      :class="[isMap && 'me-3 desktop:me-2.5', 'surface-group-title', isShelf && 'max-w-[calc(100%-6px)]']"
      :background-color="useDefaultBgColor ? undefined : skinPrimaryVariantColorAsRGB || backgroundColor"
      :can-edit="canIModerate && !isFrozen && !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop"
      :can-edit-inline="canEditSectionNameInline"
      :dark-mode="backgroundLuminance === 'dark'"
      :dir="dir"
      :edit-mode="section.id === sectionBeingRenamedId"
      :x-more-button="!isReadOnly && !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop"
      :font-id="fontId"
      :format="format"
      :title="sectionTitle"
      :is-grid="isGrid"
      :is-matrix="isMatrix"
      :is-shelf="isShelf"
      :is-stream="isStream"
      :is-map="isMap"
      :is-timeline="isTimeline"
      :is-timeline-v1="isTimelineV1"
      :is-table="isTable"
      @open-actions="handleOpenSectionActionMenu(section.id)"
      @open-actions-at-coordinates="(e) => handleOpenSectionActionMenu(section.id, e)"
      @start-editing="startEditingSectionTitle(section.id)"
      @finish-editing="finishEditingSectionTitle"
      @cancel-editing="surfaceSectionsStore.stopRenamingSection()"
      @resize="emitHeaderResize"
    />
    <div
      v-if="xSectionSpecificButtons && !hideButtons"
      :class="[
        'flex flex-row space-s-1 surface-section-action-buttons',
        shouldHideSectionButtonsWhenDragging && 'invisible',
      ]"
    >
      <component
        :is="isMap ? OzPlainButton : OzSkewedButton"
        v-for="button in sectionSpecificButtons"
        :id="button.id"
        :key="button.id"
        :data-testid="button.testId"
        :class="[
          'shrink-0',
          isMap
            ? ['w-6 h-6 p-1']
            : [
                'transition-shadow motion-reduce:transition-none',
                {
                  'hover-overlay-light': backgroundLuminance === 'light',
                  'hover-overlay-dark': backgroundLuminance === 'dark',
                },
              ],
        ]"
        :size-preset="isMap ? OzPlainButtonSizePreset.H24px : OzSkewedButtonSizePreset.H40px"
        :color-scheme="isMap && OzPlainButtonColorScheme.Secondary"
        :style="!isMap && { backgroundColor: skinPrimaryVariantColorAsRGB || backgroundColor }"
        :title="button.label"
        :aria-label="button.label"
        :dark-mode="isMap ? colorScheme === 'dark' : backgroundLuminance === 'dark'"
        :dir="dir"
        @click="button.onClick"
      >
        <OzIcon :name="button.icon" :size="16" />
      </component>
    </div>
    <OzBaseButton
      v-if="isShelf && sectionAddPostButton.shouldShow && !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop"
      ref="addPostButtonRef"
      :aria-label="sectionAddPostButton.label"
      :title="sectionAddPostButton.label"
      :class="[
        'flex',
        'items-center',
        'justify-center',

        'transition-all motion-reduce:transition-none',

        // Size & shape
        'mt-3 tablet-portrait:mt-4',
        'rounded-lg',
        'w-full h-8',
        'p-0',

        // Base
        {
          'bg-[rgba(255,255,255,0.32)]': backgroundLuminance === 'light',
          'bg-[rgba(0,0,0,0.24)]': backgroundLuminance === 'dark',
        },
        'backdrop-blur-sm',
        {
          'text-dark-text-200': backgroundLuminance === 'light',
          'text-light-text-200': backgroundLuminance === 'dark',
        },

        // Hover
        {
          'hover-hover:hover:bg-light-ui-100': backgroundLuminance === 'light',
          'hover-hover:hover:bg-dark-ui-100': backgroundLuminance === 'dark',
        },
        {
          'hover-hover:hover:text-dark-text-100': backgroundLuminance === 'light',
          'hover-hover:hover:text-light-text-100': backgroundLuminance === 'dark',
        },

        // Focus
        'focus-visible:outline-none',
        'focus-visible:ring-2',
        {
          'ring-grape-500': backgroundLuminance === 'light',
          'ring-canary-500': backgroundLuminance === 'dark',
        },

        // Active
        {
          'hover-hover:active:bg-grape-500': backgroundLuminance === 'light',
          'hover-hover:active:bg-canary-500': backgroundLuminance === 'dark',
        },
        {
          'hover-hover:active:text-light-text-100': backgroundLuminance === 'light',
          'hover-hover:active:text-dark-text-100': backgroundLuminance === 'dark',
        },
      ]"
      :data-testid="sectionAddPostButton.testId"
      @click="sectionAddPostButton.onClick"
    >
      <OzIcon :name="sectionAddPostButton.icon" :size="16" />
    </OzBaseButton>
  </header>
</template>

<style scoped>
@media (hover: hover) {
  .hover-overlay-light:hover {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.05);
  }

  .hover-overlay-dark:hover {
    box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.1);
  }
}
</style>
